import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import useReveal from "../../hooks/useReveal";
import { Play, X, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HOME_LIMIT = 3;

// Extract YouTube ID from any watch/share URL for auto-thumbnail generation.
const parseYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

export default function Capability() {
  const [data, setData] = useState(null);
  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [open, setOpen] = useState(null); // selected video object
  const { ref, visible } = useReveal();

  useEffect(() => {
    axios.get(`${API}/public/pages/home.capability`).then((r) => setData(r.data?.value || null)).catch(() => {});
    // Prefer the new /public/videos collection (managed via Admin → Videos).
    // Cap the home strip at 3 — the full library lives at /videos.
    axios.get(`${API}/public/videos?page=1&limit=${HOME_LIMIT}`).then((r) => {
      const env = r.data || {};
      const items = Array.isArray(env) ? env : env.items || [];
      const total = Array.isArray(env) ? env.length : env.total || 0;
      // Normalise to the shape this component expects: { title, url, thumbnail }
      const norm = items.map((v) => ({
        _id: v.id,
        title: v.title,
        url: v.youtube_url,
        thumbnail: v.thumbnail_url || "",
      }));
      setVideos(norm);
      setTotalVideos(total);
    }).catch(() => {});
  }, []);

  const heading = data?.heading || "Inside our capability";
  const body = data?.body || "Take a look behind the curtain — labs, lines and the people who make every product possible.";
  // Fallback chain: new Videos collection ➜ legacy pages.home.capability.videos ➜ legacy single video_url
  let list = videos;
  if (!list.length) {
    list = Array.isArray(data?.videos) ? data.videos.filter((v) => v?.url) : [];
    if (!list.length && data?.video_url) {
      list = [{ _id: "legacy", title: heading, url: data.video_url, thumbnail: "" }];
    }
  }
  // Never show more than HOME_LIMIT on the home strip
  list = list.slice(0, HOME_LIMIT);

  // Don't render section if there's nothing to show
  if (!list.length) return null;

  const resolveUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("http")) {
      const ytWatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
      if (ytWatch) return `https://www.youtube.com/embed/${ytWatch[1]}`;
      const vimeo = u.match(/vimeo\.com\/(\d+)/);
      if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
      return u;
    }
    return `${BACKEND}${u}`;
  };
  const isEmbed = (u) => /youtube\.com\/(embed|watch)|youtu\.be\/|vimeo\.com|\/embed\//.test(u || "");

  // Pick the best thumbnail source: admin-provided → YouTube auto-thumb → gradient.
  const thumbFor = (v) => {
    if (v.thumbnail) return resolveUrl(v.thumbnail);
    const yt = parseYouTubeId(v.url);
    return yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : null;
  };

  const showViewMore = totalVideos > HOME_LIMIT;

  return (
    <section ref={ref} className="relative py-20 lg:py-28 bg-white overflow-hidden" data-testid="home-capability">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-bl from-[#0EA5E9]/[0.04] to-transparent" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div
            className={`max-w-2xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C2410C]/10 text-[#C2410C] text-xs tracking-[0.2em] uppercase font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C2410C]" /> Capability
            </div>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1F2937] leading-[1.1]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {heading}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#4B5563]">{body}</p>
          </div>
          {showViewMore && (
            <Link
              to="/videos"
              className="group inline-flex items-center gap-2 text-sm font-semibold tracking-[0.14em] uppercase text-[#C2410C] hover:text-[#9A3412] transition self-start md:self-end whitespace-nowrap"
              data-testid="capability-view-more"
            >
              View all videos
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          )}
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="capability-gallery">
          {list.map((v, i) => {
            const thumb = thumbFor(v);
            return (
              <button
                key={v._id || i}
                onClick={() => setOpen(v)}
                className={`group relative aspect-video rounded-2xl overflow-hidden bg-[#0F172A] border border-[#E2E8F0] hover:border-[#C2410C] transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                style={{ transitionDelay: `${i * 120}ms` }}
                data-testid={`capability-video-${i}`}
              >
                {thumb ? (
                  <img src={thumb} alt={v.title || ""} loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                ) : !isEmbed(v.url) ? (
                  <video src={resolveUrl(v.url)} preload="metadata" muted className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C2410C]/40 via-[#0F172A] to-[#0EA5E9]/30" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/20 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <span className="w-16 h-16 rounded-full bg-[#C2410C] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
                    <Play size={26} weight="fill" />
                  </span>
                </div>
                {v.title && (
                  <div className="absolute left-5 right-5 bottom-5 text-left text-white">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/70">Watch</div>
                    <div className="mt-1 text-lg font-semibold line-clamp-2" style={{ fontFamily: "'Manrope', sans-serif" }}>{v.title}</div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {showViewMore && (
          <div className="mt-10 flex justify-center md:hidden">
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold transition"
              data-testid="capability-view-more-mobile"
            >
              View all {totalVideos} videos
              <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        )}
      </div>

      {/* Modal video player */}
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 sm:p-8 animate-[fadeUp_0.3s_ease-out]" onClick={() => setOpen(null)} data-testid="capability-modal">
          <button onClick={() => setOpen(null)} className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Close"><X size={22} weight="bold" /></button>
          <div className="w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {isEmbed(open.url) ? (
              <iframe src={resolveUrl(open.url)} title={open.title || "Video"} className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
            ) : (
              <video src={resolveUrl(open.url)} controls autoPlay className="w-full h-full" />
            )}
          </div>
        </div>
      )}
    </section>
  );
}
